import {
  type Dispatch,
  type RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { StoreApi } from 'zustand';

import { DateTimeRangePickerRoot } from './DateTimeRangePickerRoot.js';
import type {
  DateTimeInputRef,
  DateTimeRangePickerProps,
  DateTimeRangePickerRef,
} from './types.js';
import { useCallbackRef } from '../../core/hooks/useCallbackRef.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { Precision } from '../../core/time/shared.types.js';
import { getDateTimeRangePickerActions } from '../../core/time/timeframeStore/actions/dateTimeRangePicker.js';
import { createTimeframeStore } from '../../core/time/timeframeStore/createTimeframeStore.js';
import type {
  DateTimeRangePickerTimeframeStates,
  DateTimeRangePickerTimeframeStoreActions,
} from '../../core/time/timeframeStore/dateTimeRangePicker.types.js';
import { DateTimeRangePickerStoreProvider } from '../../core/time/timeframeStore/timeframeStoreProvider.js';
import { useDateTimeMessages } from '../../core/time/useDateTimeMessages.js';
import { useMinMax, useMinMaxStoreSync } from '../../core/time/useMinMax.js';
import { validate } from '../../core/time/validation.js';
import { type TimeValue } from '../../core/types/time.js';
import { getTimeOffsetFromInitialValue } from '../calendar/utils/helpers.js';
import { useFormControl } from '../forms-core/hooks/useFormControl.js';
import { useMinMaxLogger } from '../forms-core/hooks/useMinMaxLogger.js';
import {
  FormFieldMessagesAction,
  type FormFieldMessagesReducerAction,
} from '../forms-core/state/formfield-messages-state-reducer.js';
import {
  GRAIL_MAX_DATE,
  GRAIL_MIN_DATE,
} from '../forms-core/time/date-time.js';
import {
  removePrecisionFromTimeFrame,
  mapToPartialTimeframe,
} from '../forms-core/time/timeParser.js';
import type { FormControlRef, TimeRangePickerRef } from '../shared-types.js';

type DateTimeRangePickerStore = StoreApi<
  DateTimeRangePickerTimeframeStates & DateTimeRangePickerTimeframeStoreActions
>;

/**
 * Extracts the prop-to-store syncing effects into a dedicated hook so the
 * component body stays focused on wiring rather than implementation details.
 *
 * According to the zustand maintainer, the recommended way to keep a zustand
 * store in sync with React props is via useEffect.
 * see https://github.com/pmndrs/zustand/discussions/1390#discussion-4505803
 */
function useSyncPropsAndState(
  store: DateTimeRangePickerStore,
  memoedValue: {
    from: TimeValue | null;
    to: TimeValue | null;
  } | null,
  isControlled: boolean,
  precision: Exclude<Precision, 'day'>,
  formatString: string | undefined,
  required: boolean | undefined,
  formFieldMessagesDispatch:
    | Dispatch<FormFieldMessagesReducerAction>
    | undefined,
  dateTimeRangePickerRef: RefObject<DateTimeRangePickerRef | null>,
) {
  // Sync the controlled value into the store whenever it changes.
  useEffect(() => {
    isControlled &&
      store.setState((state) => {
        const error = validate(memoedValue ?? null, state.min, state.max, {
          formMessageDispatch: formFieldMessagesDispatch,
          precision: state.precision,
          showError: false,
          checkOrder: true,
          formerError: state.calendarError,
          ref: dateTimeRangePickerRef,
          expectedFormat: state.formatString,
          valueMode: 'dateTimePicker',
          required: state.required,
        });

        const value = removePrecisionFromTimeFrame(
          memoedValue,
          state.precision,
        );
        const timeOffset = getTimeOffsetFromInitialValue(
          value,
          'range',
          state.min,
          state.max,
          precision,
        );

        return {
          ...state,
          dateTimeRangePickerTimeframe: value,
          calendarValue: value,
          calendarError: error,
          dateTimeRangePickerError: error,
          timeOffset,
        };
      });
  }, [
    formFieldMessagesDispatch,
    isControlled,
    memoedValue,
    store,
    precision,
    dateTimeRangePickerRef,
  ]);

  // Sync precision and formatString into the store whenever they change.
  useEffect(() => {
    store.setState((state) => {
      const value = removePrecisionFromTimeFrame(
        state.dateTimeRangePickerTimeframe,
        precision,
      );

      return {
        ...state,
        precision,
        formatString,
        dateTimeRangePickerTimeframe: value,
        calendarValue: value,
        required,
      };
    });
  }, [precision, formatString, store, required]);
}

/**
 * Component to comfortably enter a timeframe in localized format for expressions.
 * @internal
 */
export const DateTimeRangePicker = forwardRef<
  DateTimeRangePickerRef,
  DateTimeRangePickerProps
>(
  (
    {
      value: propValue,
      defaultValue: propDefaultValue,
      onChange,
      min: propMin = GRAIL_MIN_DATE,
      max: propMax = GRAIL_MAX_DATE,
      precision = 'minutes',
      disabled: propDisabled,
      required: propRequired,
      readOnly: propReadOnly,
      id: propId,
      'aria-disabled': propAriaDisabled,
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- APPDEV-18321
      controlState,
      onBlur,
      ...remainingProps
    },
    forwardedRef,
  ) => {
    const [isControlled] = useState(propValue !== undefined);
    const onChangeProp = useCallbackRef(onChange);

    const memoedValue = useMemo(
      () => mapToPartialTimeframe(propValue, precision),
      [precision, propValue],
    );
    const memoedDefaultValue = useMemo(
      () => mapToPartialTimeframe(propDefaultValue, precision),
      /** We're ignoring updates to defaultValue as it only considers the initial state. */
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    // Keep min and max updated at midnight
    const { min, max } = useMinMax(propMin, propMax);
    useMinMaxLogger(min, max, 'DateTimeRangePicker');

    const calendarRef = useRef<
      | FormControlRef<HTMLDivElement>
      | (Omit<FormControlRef<HTMLDivElement>, 'inputRef'> &
          Pick<TimeRangePickerRef<HTMLDivElement>, 'inputRef'>)
    >(null);
    const dateTimeFromRef = useRef<DateTimeInputRef>(null);
    const dateTimeRangePickerRef = useRef<DateTimeRangePickerRef>(null);

    const formControl = useFormControl({
      propDisabled,
      propRequired,
      propId,
      propAriaDisabled,
      propReadOnly,
      controlState,
    });
    const { required, messageDispatch: formFieldMessagesDispatch } =
      formControl;

    const { formatString } = useDateTimeMessages({ precision });

    const [initialState] = useState<DateTimeRangePickerTimeframeStates>(() => {
      const value = isControlled ? memoedValue : memoedDefaultValue;
      const timeOffset = getTimeOffsetFromInitialValue(
        value,
        'range',
        min,
        max,
        precision,
      );

      const error = validate(value, min, max, {
        formMessageDispatch: formFieldMessagesDispatch,
        precision,
        showError: false,
        checkOrder: true,
        ref: dateTimeRangePickerRef,
        expectedFormat: formatString,
        valueMode: 'dateTimePicker',
        required,
      });

      return {
        calendarValue: value,
        dateTimeRangePickerTimeframe: value,
        min,
        max,
        precision,
        calendarRef,
        dateTimeFromRef,
        dateTimeRangePickerRef,
        calendarError: error,
        dateTimeRangePickerError: error,
        timeOffset,
        formatString,
        required,
      };
    });

    const [store] = useState(() =>
      createTimeframeStore<
        DateTimeRangePickerTimeframeStates,
        DateTimeRangePickerTimeframeStoreActions
      >(
        initialState,
        getDateTimeRangePickerActions(
          isControlled,
          formFieldMessagesDispatch,
          onChangeProp,
        ),
      ),
    );

    useSyncPropsAndState(
      store,
      memoedValue,
      isControlled,
      precision,
      formatString,
      required,
      formFieldMessagesDispatch,
      dateTimeRangePickerRef,
    );

    // Keep min and max updated at midnight
    useMinMaxStoreSync(
      store,
      min,
      max,
      useCallback(
        (
          state: DateTimeRangePickerTimeframeStates,
          min: TimeValue | null,
          max: TimeValue | null,
        ) => {
          const error = validate(state.dateTimeRangePickerTimeframe, min, max, {
            formMessageDispatch: formFieldMessagesDispatch,
            formerError: state.calendarError,
            precision: state.precision,
            showError: false,
            checkOrder: true,
            ref: dateTimeRangePickerRef,
            expectedFormat: state.formatString,
            valueMode: 'dateTimePicker',
            required: state.required,
          });
          return { calendarError: error, dateTimeRangePickerError: error };
        },
        [formFieldMessagesDispatch],
      ),
    );

    const mergedRef = useMergeRefs<DateTimeRangePickerRef>([
      dateTimeRangePickerRef,
      forwardedRef,
    ]);

    return (
      <DateTimeRangePickerStoreProvider store={store}>
        <DateTimeRangePickerRoot
          {...remainingProps}
          ref={mergedRef}
          formControl={formControl}
          onBlur={(event) => {
            onBlur?.(event);

            if (!event.target.contains(document.activeElement)) {
              formFieldMessagesDispatch?.({
                type: FormFieldMessagesAction.SETISTOUCHED,
                payload: { isTouched: true },
              });
            }
          }}
        />
      </DateTimeRangePickerStoreProvider>
    );
  },
);
